import axios, { AxiosError, AxiosResponse } from 'axios';
import { EErrorStatus, EStorageKey } from 'common/const/axios.enum';
import { IAxiosResponseData } from 'common/models';
import { showErrorMessage } from 'common/helpers/message.helper';
import { store } from 'app/store';
import { IToken } from 'entities/Auth/Auth.models';
import { clearCreds, saveCreds } from 'entities/Auth/Auth.helper';
import { authTransport } from 'entities/Auth/Auth.transport';

let captchaToken: string | null = null;

export const setCaptchaToken = (token: string | null): void => {
  captchaToken = token;
};

export const getStorageItem = <T>(key: EStorageKey): T | null => {
  const item = localStorage.getItem(key);
  return item ? JSON.parse(item) : null;
};

export const saveStorageItem = (key: EStorageKey, value: IToken | number | string): void => {
  localStorage.setItem(key, JSON.stringify(value));
};

export const removeStorageItem = (key: EStorageKey): void => {
  localStorage.removeItem(key);
};

export const initAxios = () => {
  axios.defaults.baseURL = `/api`;
  axios.interceptors.request.use(async (config) => {
    const creds = getStorageItem<IToken>(EStorageKey.Creds);
    const xUserId = getStorageItem<string>(EStorageKey.XUserId);
    const xBasketId = getStorageItem<string>(EStorageKey.XBasketId);

    if (creds) {
      config.headers.Authorization = `Bearer ${creds.access.token}`;
    }

    if (xUserId) {
      config.headers['x-user-id'] = xUserId;
    }

    if (
      xBasketId &&
      (config.url?.includes('catalog') ||
        config.url?.includes('category') ||
        config.url?.includes('property/by-category') ||
        (config.url?.includes('good') && config.url !== 'good/brands') ||
        config.url?.includes('search') ||
        (config.url?.includes('basket') && config.url !== 'basket' && config.url !== 'basket/subdivisions'))
    ) {
      config.headers['x-basket-id'] = xBasketId;
    }

    if (captchaToken) {
      config.headers['x-captcha-token'] = captchaToken;
      setCaptchaToken(null);
    }

    return config;
  });

  axios.interceptors.response.use(
    (response: AxiosResponse) => response.data,
    async (error) => {
      const { config, response } = error;

      if (response) {
        if (config.url === 'auth' && config.method === 'put') {
          clearCreds();
          return Promise.reject(error);
        } else if (config.url !== 'auth' && response.status === EErrorStatus.Unauthorized) {
          const creds = getStorageItem<IToken>(EStorageKey.Creds);

          if (creds?.refresh.token) {
            const refreshResponse = await authTransport.refreshToken(creds.refresh.token);

            if (refreshResponse) {
              await saveCreds(refreshResponse);
              config.headers.Authorization = `Bearer ${creds.access?.token}`;
              return axios(config);
            } else {
              clearCreds();
              return Promise.reject(error);
            }
          } else {
            clearCreds();
            return Promise.reject(error);
          }
        } else {
          return Promise.reject(error);
        }
      } else {
        return Promise.reject(error);
      }
    },
  );
};

export const axiosErrorHandler = (error: unknown, setError?: (payload: string) => any) => {
  const response = (error as AxiosError).response as AxiosResponse<IAxiosResponseData | undefined>;

  if (response) {
    const { data, status, statusText } = response;

    if (data) {
      if (setError && status !== EErrorStatus.InternalServerError) {
        store.dispatch(setError(data.message));
      } else {
        showErrorMessage(data.message || data.error);
      }
    } else {
      showErrorMessage(statusText);
    }
  } else {
    showErrorMessage('Произошла непредвиденная ошибка.');
  }
};
